import React, { useState, useEffect } from 'react';
import { acceptTicketMilestone, triggeredServiceRequest } from '../../../actions/service_request';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import ButtonScogoClosedOutlined from '../../../common/Buttons/ButtonScogoClosedOutlined';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import { SEND_TOAST } from '../../../types/toast';
import Assign from '../../Ticket/Modal/Assign';
import moment from 'moment';
import { useCallback } from 'react';
import { debounce } from '../../../utils/common';
import { TicketAgentRole } from '../../Ticket/TicketController';
import { MilestoneSubStatusColor } from '../../Ticket/TicketColumns/Status';

export default function AssignTicketToUser({ user_id, full_name }) {
    const dispatch = useDispatch();
    const [step, setStep] = useState(1);
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [currentTicketIndex, setCurrentTicketIndex] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);

    const triggeredServiceRequestList = useSelector((state) => state.serviceRequest.triggeredServiceRequestList);
    const isTriggeredServiceRequestLoading = useSelector((state) => state.loading.isTriggeredServiceRequestLoading);

    const getTicketList = useCallback((query) => {
        dispatch(triggeredServiceRequest({ user_id: user_id, limit: 100, page: 1, query }));
    }, [dispatch, user_id]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedGetTicketList = useCallback(debounce(getTicketList, 500), [getTicketList]);

    useEffect(() => {
        getTicketList(searchQuery);
    }, [getTicketList, searchQuery]);

    const handleTicketSelect = (ticket) => {
        setSelectedTickets(prev => {
            const isSelected = prev.find(t => t.id === ticket.id);
            if (isSelected) {
                return prev.filter(t => t.id !== ticket.id);
            }
            if (!ticket.agents) {
                ticket.agents = [];
            }
            ticket.actions = []
            return [...prev, ticket];
        });
    };

    const handleContinue = () => {
        if (selectedTickets.length === 0) {
            dispatch({ type: SEND_TOAST, payload: { status: 'danger', message: "Please select at least one ticket" } });
            return;
        }
        setStep(2);
        setCurrentTicketIndex(0);
    };

    const handleBack = () => {
        setStep(1);
    };

    const renderTicketCard = (ticket) => {
        const isSelected = selectedTickets.find(t => t.id === ticket.id);

        return (
            <div
                key={ticket.id}
                className={`p-3 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${isSelected ? 'border-scogoprimary bg-blue-50' : 'border-gray-200 bg-white'}`}
                onClick={() => handleTicketSelect(ticket)}
            >
                <div className="flex items-start gap-2">
                    <div className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-scogoprimary bg-scogoprimary' : 'border-gray-300'}`}>
                        {isSelected && (
                            <span className="material-icons text-white" style={{ fontSize: '12px' }}>check</span>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className='flex items-center gap-2'>

                            <h3 className="font-semibold text-gray-800 text-md mb-1.5">{ticket.ticket_number}</h3>
                            {ticket.milestones && ticket.milestones.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {ticket.milestones.map((milestone) => {
                                        let milestoneStatus = milestone.milestone_status;
                                        const colorClasses = MilestoneSubStatusColor[milestoneStatus];
                                        const typeLabel = milestone.type ? milestone.type.replace(/_/g, ' ') : 'Milestone';
                                        const statusLabel = (milestoneStatus || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());

                                        return (
                                            <div
                                                key={milestone.id}
                                                className={`flex items-center gap-1 px-2 rounded border text-xs font-medium ${colorClasses}`}
                                                title={typeLabel + ": " + statusLabel}
                                            >
                                                <span>{typeLabel}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        {ticket.location?.address ? (
                            <p className="text-sm text-gray-500 mb-1.5 flex items-start gap-1 line-clamp-2">
                                <span className="material-icons flex-shrink-0" style={{ fontSize: '14px' }}>location_on</span>
                                <span className="break-words">{ticket.location.address}</span>
                            </p>
                        ) : null}
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                                <span className="material-icons" style={{ fontSize: '14px' }}>business</span>
                                <span className="truncate max-w-[200px]">{ticket.project?.name || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="material-icons" style={{ fontSize: '14px' }}>calendar_today</span>
                                <span>{moment(ticket.created_at).format('DD MMM YYYY')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const onAssignClick = (agents) => {
        const milestoneIds = selectedTickets[currentTicketIndex].milestones.map(milestone => milestone.id);
        dispatch(acceptTicketMilestone({ ticketId: selectedTickets[currentTicketIndex].id, milestoneIds, payload: { agents }, formName: 'assign_ticket_to_user', source: 'assign_ticket_to_user' }));

    };

    return (
        <div className='bg-white rounded-md relative flex flex-col h-full max-h-[80vh]'>
            {step === 1 ? (
                <div className="flex flex-col h-full transition-opacity duration-300 ease-in-out">
                    {/* Header */}
                    <div className="px-6 py-3 border-b">
                        <h2 className="text-lg font-semibold text-gray-800">Select Tickets to Assign</h2>
                        <p className="text-xs text-gray-500 mt-0.5">Choose one or more tickets to assign to this user</p>
                    </div>

                    {/* Search Bar */}
                    <div className="px-6 py-2 border-b bg-gray-50">
                        <div className="relative">
                            <span className="material-icons absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                                search
                            </span>
                            <input
                                type="text"
                                placeholder="Search by ticket ID, title, or organization..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-scogoprimary"
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    debouncedGetTicketList(e.target.value);
                                }}
                            />
                        </div>
                    </div>

                    {/* Tickets List */}
                    <div className="overflow-y-auto px-6 py-3">
                        {isTriggeredServiceRequestLoading ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-scogoprimary"></div>
                            </div>
                        ) : triggeredServiceRequestList.length > 0 ? (
                            <div className="space-y-2 max-h-[20rem] overflow-y-auto">
                                {triggeredServiceRequestList.map(ticket => renderTicketCard(ticket))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                <span className="material-icons text-6xl mb-2">inbox</span>
                                <p className="text-lg">No tickets found</p>
                            </div>
                        )}
                    </div>

                    {/* Selected Count Badge */}
                    {selectedTickets.length > 0 && (
                        <div className="px-6 py-1.5 bg-blue-50 border-t border-blue-200 transition-all duration-300 ease-in-out">
                            <p className="text-xs text-scogoprimary font-medium">
                                {selectedTickets.length} ticket{selectedTickets.length > 1 ? 's' : ''} selected
                            </p>
                        </div>
                    )}

                    {/* Footer Actions */}
                    <div className='flex justify-end gap-3 px-6 py-3 border-t'>
                        <ButtonScogoClosedOutlined
                            textOrComponent='Cancel'
                            onClick={() => dispatch(closeModalAction())}
                        />
                        <ButtonScogoPrimary
                            textOrComponent={`Continue (${selectedTickets.length})`}
                            onClick={handleContinue}
                            disabled={selectedTickets.length === 0}
                        />
                    </div>
                </div>
            ) : (
                <div className="flex flex-col h-full transition-opacity duration-300 ease-in-out">
                    {/* Header with Progress */}
                    <div className="px-6 py-4 border-b bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleBack}
                                    className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                                >
                                    <span className="material-icons">arrow_back</span>
                                </button>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-800">Assign Ticket</h2>
                                    <p className="text-sm text-gray-500">
                                        Ticket {currentTicketIndex + 1} of {selectedTickets.length}: {selectedTickets[currentTicketIndex]?.ticket_id}
                                    </p>
                                </div>
                            </div>
                            <div className="text-sm font-medium text-scogoprimary">
                                {currentTicketIndex + 1} / {selectedTickets.length}
                            </div>
                        </div>
                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-scogoprimary h-2 rounded-full transition-all duration-300 ease-in-out"
                                style={{ width: `${((currentTicketIndex + 1) / selectedTickets.length) * 100}%` }}
                            />
                        </div>
                    </div>

                    {/* Assign Component */}
                    <div className="flex-1 overflow-auto">
                        {selectedTickets[currentTicketIndex] && (
                            <Assign onAccept={onAssignClick} ticket={selectedTickets[currentTicketIndex]} newTicketaccept agent={{ id: user_id, full_name }} source={'assign_ticket_to_user'} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}